import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useLanguage } from '@/i18n/LanguageContext';
import { SkuMeta } from '@/components/SkuMeta';
import { formatNumber } from '@/i18n/format';
import { newOpId, plainQty, toScaled } from '@/pages/Exceptions/shared';
import { ScanCapture, type ScanCaptureHandle } from '@/scan/ScanCapture';
import { ScanBannerView, type ScanBanner } from '@/scan/ScanBanner';
import { QtyStepper, qtyToSend } from '@/scan/QtyStepper';
import { candidateMeta, errorBanner, productLabel } from '@/scan/scanModel';
import type { InboundLine, ScanLocation, ScanProductCandidate, ScanReceiveInput, ScanReceiveMode, ScanReceiveResult, ScanResolution } from '@/api/types';
import { discrepancyLabel, partyLabel, type DiscrepancyCode, type PartyCode } from './receivingModel';

const STORAGE_SCAN_MODE = 'wms_ops_scan_mode';

/** What the operator may flag from a scan. WRONG_ITEM / NO_BARCODE need a product pick, which is the manual panel's job. */
const QUICK_DISCREPANCIES: readonly DiscrepancyCode[] = ['NONE', 'DAMAGED', 'EXPIRED', 'SHORTAGE', 'OVERAGE'];
/** Goods that must not become sellable stock never go straight into a cell: only quantity differences do (server: SCAN.DISCREPANCY_NEEDS_RECEIVING). */
const CELL_DISCREPANCIES: readonly DiscrepancyCode[] = ['NONE', 'SHORTAGE', 'OVERAGE'];
const QUICK_PARTIES: readonly PartyCode[] = ['UNDECIDED', 'WAREHOUSE', 'OWNER', 'CARRIER'];

type Snapshot = { readonly mode: ScanReceiveMode; readonly qty: string; readonly discrepancy: DiscrepancyCode; readonly party: PartyCode };
type CellTotal = { readonly id: string; readonly code: string; readonly qty: number };
type LastItem = { readonly label: string; readonly qty: string; readonly destination: string };
type Overlay = Readonly<Record<string, { readonly received: string; readonly discrepancy: string }>>;

function readScanMode(): boolean {
  try {
    return localStorage.getItem(STORAGE_SCAN_MODE) === '1';
  } catch {
    return false;
  }
}
function writeScanMode(on: boolean): void {
  try {
    localStorage.setItem(STORAGE_SCAN_MODE, on ? '1' : '0');
  } catch {
    // private mode: the choice lives for this tab only
  }
}

/** Per-line figures after the scans of this session (the server's own numbers, never computed here). */
export function progressOf(lines: readonly InboundLine[]): { received: number; expected: number; percent: number } {
  let received = 0;
  let expected = 0;
  for (const l of lines) {
    received += Number(l.qty_received);
    expected += Number(l.qty_expected);
  }
  const percent = expected > 0 ? Math.min(100, Math.round((received / expected) * 100)) : 0;
  return { received, expected, percent };
}

/**
 * Receiving v2 by scanner (Ops inbound page). Two modes behind one switch at the top of the receiving area:
 *  - «Принимать сразу в ячейку» (TO_LOCATION): scan a CELL, then each product goes receipt + putaway into that cell
 *    in one server transaction; scanning another cell switches the active cell;
 *  - «Принять без размещения» (TO_RECEIVING, the default and the old behaviour): products go to the receiving zone.
 * Every code goes through the server's resolver first (a cell label, a product barcode, or both/neither), so the
 * page never guesses what a code is. Scans are processed strictly in order and never dropped; one fresh opId per
 * physical scan, the same opId only when a failed request is retried.
 */
export function ScanReceivePanel({
  inboundId, number, ownerId, ownerName, warehouseId, warehouseName, receivingCode, lines, canPutaway, onChanged,
}: {
  readonly inboundId: string;
  readonly number: string;
  readonly ownerId: string;
  readonly ownerName: string;
  readonly warehouseId: string;
  readonly warehouseName: string;
  readonly receivingCode: string | null;
  readonly lines: readonly InboundLine[];
  /** Receiving straight into a cell also performs the putaway leg: it needs PUTAWAY.WRITE as well. */
  readonly canPutaway: boolean;
  /** Re-read the inbound without blanking the page (receipts history, putaway card). */
  readonly onChanged: () => void;
}): JSX.Element {
  const { t, locale } = useLanguage();
  const [scanOn, setScanOn] = useState(readScanMode);
  const [mode, setMode] = useState<ScanReceiveMode>('TO_RECEIVING');
  const [active, setActive] = useState<{ id: string; code: string } | null>(null);
  const [cells, setCells] = useState<readonly CellTotal[]>([]);
  const [banner, setBanner] = useState<ScanBanner | null>(null);
  const [last, setLast] = useState<LastItem | null>(null);
  const [lastCode, setLastCode] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  const [qty, setQty] = useState('1');
  const [discrepancy, setDiscrepancy] = useState<DiscrepancyCode>('NONE');
  const [party, setParty] = useState<PartyCode>('UNDECIDED');
  const [discOpen, setDiscOpen] = useState(false);
  const [overlay, setOverlay] = useState<Overlay>({});
  const [choice, setChoice] = useState<{ code: string; snap: Snapshot; candidates: readonly ScanProductCandidate[] } | null>(null);
  const [hintCell, setHintCell] = useState<ScanLocation | null>(null);
  const [ambiguousCode, setAmbiguousCode] = useState<{ code: string; snap: Snapshot } | null>(null);

  const capture = useRef<ScanCaptureHandle>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const activeRef = useRef(active);
  activeRef.current = active;
  const retryRef = useRef<(() => Promise<void>) | null>(null);
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;

  // A fresh read of the inbound replaces what the scans of this session patched in.
  useEffect(() => {
    setOverlay({});
  }, [lines]);

  const effectiveLines = useMemo<readonly InboundLine[]>(
    () => lines.map((l) => {
      const o = overlay[l.id];
      return o ? { ...l, qty_received: o.received, discrepancy: o.discrepancy } : l;
    }),
    [lines, overlay],
  );
  const progress = progressOf(effectiveLines);

  function toggleScan(on: boolean): void {
    setScanOn(on);
    writeScanMode(on);
  }

  const enqueue = useCallback((task: () => Promise<void>) => {
    setPending((n) => n + 1);
    queue.current = queue.current.then(task).catch(() => undefined).finally(() => setPending((n) => n - 1));
  }, []);

  const showError = useCallback((err: unknown, retry: (() => Promise<void>) | null) => {
    const b = errorBanner(t, err);
    retryRef.current = b.retryable ? retry : null;
    setBanner(b.retryable && retry ? { ...b, actions: [{ id: 'retry', label: t('scan.retry') }] } : b);
  }, [t]);

  async function send(input: ScanReceiveInput, label: string, expectedBefore: number | null): Promise<void> {
    try {
      const res: ScanReceiveResult = await endpoints.inboundScanReceive(inboundId, input);
      retryRef.current = null;
      applyReceived(res, input, label, expectedBefore);
    } catch (err) {
      showError(err, () => send(input, label, expectedBefore));
    }
  }

  function applyReceived(res: ScanReceiveResult, input: ScanReceiveInput, label: string, remainingBefore: number | null): void {
    const added = Number(input.qty ?? '1');
    setOverlay((o) => ({ ...o, [res.line.inboundLineId]: { received: res.line.qtyReceived, discrepancy: res.line.discrepancy } }));
    const dest = res.destination.code;
    if (res.mode === 'TO_LOCATION') {
      setCells((cs) => {
        const hit = cs.find((c) => c.id === res.destination.locationId);
        return hit
          ? cs.map((c) => (c.id === hit.id ? { ...c, qty: c.qty + added } : c))
          : [...cs, { id: res.destination.locationId, code: dest, qty: added }];
      });
    }
    setLast({ label, qty: plainQty(input.qty ?? '1'), destination: dest });
    setLastCode(input.productBarcode ?? res.line.skuCode);
    setQty('1');
    setDiscrepancy('NONE');
    setParty('UNDECIDED');
    setDiscOpen(false);
    const wasFull = remainingBefore !== null && remainingBefore <= 0;
    const detailParts: string[] = [
      t('scanRcv.accepted.progress', { received: plainQty(res.line.qtyReceived), expected: plainQty(res.line.qtyExpected) }),
    ];
    let tone: ScanBanner['tone'] = 'ok';
    if (res.overage || wasFull) {
      tone = 'warn';
      detailParts.push(wasFull ? t('scanRcv.alreadyFull') : t('scanRcv.overage'));
    }
    if (res.repeatOfRecent) detailParts.push(t('scanRcv.repeatHint'));
    setBanner({ tone, title: t('scanRcv.accepted', { name: label, qty: plainQty(input.qty ?? '1'), cell: dest }), detail: detailParts.join(' ') });
    onChanged();
  }

  async function receiveProduct(c: ScanProductCandidate, code: string, snap: Snapshot, byPick: boolean): Promise<void> {
    const loc = activeRef.current;
    if (c.isSerialTracked || c.isLotTracked) {
      setBanner({ tone: 'error', title: t('scan.err.tracked') });
      return;
    }
    const input: ScanReceiveInput = {
      opId: newOpId(),
      mode: snap.mode,
      ...(snap.mode === 'TO_LOCATION' && loc ? { locationId: loc.id } : {}),
      ...(byPick ? { skuId: c.skuId } : { productBarcode: code }),
      qty: qtyToSend(snap.qty),
      ...(snap.discrepancy !== 'NONE' ? { discrepancy: snap.discrepancy, responsibleParty: snap.party } : {}),
    };
    // Is the matching line already complete? The scan is still accepted (nothing is ever dropped); it is flagged.
    const row = linesRef.current.find((l) => l.sku_id === c.skuId);
    const received = row ? Number((overlayRef.current[row.id]?.received ?? row.qty_received)) : 0;
    const remaining = row ? Number(row.qty_expected) - received : null;
    await send(input, productLabel(c), remaining);
  }

  async function handle(code: string, snap: Snapshot, expect?: 'LOCATION' | 'PRODUCT'): Promise<void> {
    let res: ScanResolution;
    try {
      res = await endpoints.scanResolve({ warehouseId, code, ownerId, ...(expect ? { expect } : {}) });
    } catch (err) {
      showError(err, () => handle(code, snap, expect));
      return;
    }
    retryRef.current = null;
    setChoice(null);
    setAmbiguousCode(null);
    setHintCell(null);
    switch (res.kind) {
      case 'LOCATION': {
        const loc = res.location;
        if (snap.mode === 'TO_RECEIVING') {
          setHintCell(loc);
          setBanner({
            tone: 'info',
            title: t('scanRcv.location.detected', { code: loc.code }),
            detail: t('scanRcv.location.hint'),
            actions: loc.isStorage ? [{ id: 'switchMode', label: t('scanRcv.location.switch') }] : undefined,
          });
          return;
        }
        if (!loc.isStorage) {
          setBanner({ tone: 'error', title: t('scanRcv.location.detected', { code: loc.code }), detail: t('put.err.destination') });
          return;
        }
        activateCell(loc);
        return;
      }
      case 'ARCHIVED_LOCATION':
        setBanner({ tone: 'error', title: t('scan.err.archived'), detail: res.location.code });
        return;
      case 'UNKNOWN':
        setBanner({ tone: 'error', title: t('scan.err.unknown'), detail: code });
        return;
      case 'AMBIGUOUS':
        setAmbiguousCode({ code, snap });
        setBanner({
          tone: 'info',
          title: t('scan.ambiguous.title'),
          detail: code,
          actions: [{ id: 'amb:LOCATION', label: t('scan.ambiguous.location') }, { id: 'amb:PRODUCT', label: t('scan.ambiguous.product') }],
        });
        return;
      case 'PRODUCT': {
        const cands = res.candidates;
        const first = cands[0];
        if (!first) {
          setBanner({ tone: 'error', title: t('scan.err.unknown'), detail: code });
          return;
        }
        if (snap.mode === 'TO_LOCATION' && !activeRef.current) {
          setBanner({ tone: 'warn', title: t('scanRcv.needCell'), detail: t('scanRcv.product.detected', { name: productLabel(first) }) });
          return;
        }
        if (cands.length > 1) {
          setChoice({ code, snap, candidates: cands });
          setBanner({
            tone: 'info',
            title: t('scan.pickProduct'),
            detail: code,
            actions: cands.map((c, i) => ({ id: `pick:${i}`, label: `${productLabel(c)} — ${c.ownerName}` })),
          });
          return;
        }
        await receiveProduct(first, code, snap, false);
        return;
      }
      default:
    }
  }

  function activateCell(loc: ScanLocation): void {
    setActive({ id: loc.locationId, code: loc.code });
    activeRef.current = { id: loc.locationId, code: loc.code };
    setCells((cs) => (cs.some((c) => c.id === loc.locationId) ? cs : [...cs, { id: loc.locationId, code: loc.code, qty: 0 }]));
    setBanner({ tone: 'ok', title: t('scanRcv.location.detected', { code: loc.code }), detail: t('scanRcv.location.detail') });
  }

  function onScan(code: string): void {
    setLastCode(code);
    const snap: Snapshot = { mode, qty, discrepancy, party };
    enqueue(() => handle(code, snap));
  }

  function onAction(id: string): void {
    if (id === 'retry') {
      const run = retryRef.current;
      if (run) enqueue(run);
    } else if (id === 'switchMode') {
      setMode('TO_LOCATION');
      if (hintCell?.isStorage) activateCell(hintCell);
      setHintCell(null);
    } else if (id.startsWith('amb:') && ambiguousCode) {
      const expect = id === 'amb:LOCATION' ? 'LOCATION' : 'PRODUCT';
      const { code, snap } = ambiguousCode;
      enqueue(() => handle(code, snap, expect));
    } else if (id.startsWith('pick:') && choice) {
      const c = choice.candidates[Number(id.slice(5))];
      if (c) {
        const { code, snap } = choice;
        setChoice(null);
        enqueue(() => receiveProduct(c, code, snap, true));
      }
    }
  }

  const destinationText = mode === 'TO_RECEIVING'
    ? t('scanRcv.dest.receiving', { code: receivingCode ?? '—', warehouse: warehouseName })
    : null;
  const activeTotal = active ? (cells.find((c) => c.id === active.id)?.qty ?? 0) : 0;
  const busy = pending > 0;

  return (
    <section className="card scan-panel" aria-labelledby="scan-panel-title" data-testid="scan-receive">
      <div className="scan-panel-head">
        <h2 id="scan-panel-title" className="card-title">{t('scanRcv.title')}</h2>
        <div className="meta" data-testid="scan-request">{t('scanRcv.request', { number, seller: ownerName })}</div>
      </div>

      <div role="radiogroup" aria-label={t('scanRcv.mode.label')} className="scan-mode-switch" data-testid="receive-mode">
        {(['TO_LOCATION', 'TO_RECEIVING'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={mode === m}
            disabled={m === 'TO_LOCATION' && !canPutaway}
            title={m === 'TO_LOCATION' && !canPutaway ? t('scanRcv.mode.needPutaway') : undefined}
            className={mode === m ? 'scan-mode-option active' : 'scan-mode-option'}
            onClick={() => { setMode(m); if (m === 'TO_LOCATION' && !CELL_DISCREPANCIES.includes(discrepancy)) setDiscrepancy('NONE'); capture.current?.focus(); }}
          >
            {t(`scanRcv.mode.${m}` as const)}
          </button>
        ))}
      </div>

      <label className="scan-toggle">
        <input type="checkbox" role="switch" checked={scanOn} onChange={(e) => toggleScan(e.target.checked)} />
        <span>{t('scan.mode')}</span>
      </label>
      {!scanOn && <p className="meta">{t('scan.mode.hint')}</p>}

      {destinationText && <p className="scan-destination" data-testid="scan-destination">{destinationText}</p>}

      {scanOn && (
        <>
          <ScanCapture
            ref={capture}
            enabled={scanOn}
            busy={busy}
            lastCode={lastCode}
            onScan={(code) => onScan(code)}
            onDuplicate={() => setBanner({ tone: 'info', title: t('scan.duplicate'), detail: t('scan.duplicate.detail') })}
          />

          {mode === 'TO_LOCATION' && (
            <div className={active ? 'scan-active-cell' : 'scan-active-cell scan-active-cell--none'} data-testid="active-cell">
              {active ? (
                <>
                  <div className="scan-active-label">{t('scanRcv.active.cell')}</div>
                  <div className="scan-active-value">{t('scanRcv.active.line', { code: active.code, n: formatNumber(activeTotal, locale) })}</div>
                </>
              ) : (
                <div className="scan-active-value">{t('scanRcv.needCell')}</div>
              )}
            </div>
          )}

          <ScanBannerView banner={banner} onAction={onAction} />

          <div className="scan-controls">
            <QtyStepper value={qty} onChange={setQty} onDone={() => capture.current?.focus()} />
            <div className="scan-disc">
              <button type="button" aria-expanded={discOpen} onClick={() => setDiscOpen((o) => !o)}>
                {discrepancy === 'NONE' ? t('scanRcv.disc.btn') : `${t('scanRcv.disc.btn')}: ${discrepancyLabel(t, discrepancy)}`}
              </button>
              {discOpen && (
                <div className="scan-disc-body" role="group" aria-label={t('scanRcv.disc.title')}>
                  <label>
                    {t('rcv.discrepancy')}
                    <select value={discrepancy} onChange={(e) => setDiscrepancy(e.target.value as DiscrepancyCode)}>
                      {(mode === 'TO_LOCATION' ? CELL_DISCREPANCIES : QUICK_DISCREPANCIES).map((d) => <option key={d} value={d}>{d === 'NONE' ? t('scanRcv.disc.none') : discrepancyLabel(t, d)}</option>)}
                    </select>
                  </label>
                  {discrepancy !== 'NONE' && (
                    <label>
                      {t('rcv.party')}
                      <select value={party} onChange={(e) => setParty(e.target.value as PartyCode)}>
                        {QUICK_PARTIES.map((p) => <option key={p} value={p}>{partyLabel(t, p)}</option>)}
                      </select>
                    </label>
                  )}
                  <p className="meta">{t('scanRcv.disc.next')}</p>
                </div>
              )}
            </div>
          </div>

          {last && (
            <div className="scan-last" data-testid="scan-last">
              <span className="scan-last-label">{t('scanRcv.last')}</span> {last.label} +{last.qty} → {last.destination}
            </div>
          )}

          {mode === 'TO_LOCATION' && cells.length > 0 && (
            <div className="scan-cells" data-testid="scan-cells">
              <div className="scan-cells-title">{t('scanRcv.cells.title')}</div>
              <ul>
                {cells.map((c) => (
                  <li key={c.id} className={active?.id === c.id ? 'active' : undefined}>{t('scanRcv.cells.item', { code: c.code, n: formatNumber(c.qty, locale) })}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <div className="scan-progress" data-testid="scan-progress">
        <div className="scan-progress-text">{t('scanRcv.progress', { received: plainQty(String(progress.received)), expected: plainQty(String(progress.expected)) })}</div>
        <div className="scan-progress-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.percent} aria-label={t('scanRcv.progress.label')}>
          <div className="scan-progress-fill" style={{ width: `${progress.percent}%` }} />
        </div>
      </div>

      <div className="table-scroll">
        <table data-testid="scan-lines">
          <thead>
            <tr>
              <th>{t('rcv.colProduct')}</th>
              <th>{t('scanRcv.col.expected')}</th>
              <th>{t('scanRcv.col.accepted')}</th>
              <th>{t('scanRcv.col.disc')}</th>
            </tr>
          </thead>
          <tbody>
            {effectiveLines.map((l) => {
              const short = toScaled(l.qty_received) < toScaled(l.qty_expected);
              return (
                <tr key={l.id} data-testid="scan-line">
                  <td>
                    <div>{l.sku_name ?? t('rcv.unidentified')}</div>
                    <SkuMeta code={l.sku_code} article={l.article} size={l.size} color={l.color} barcodes={l.barcodes} />
                  </td>
                  <td>{plainQty(l.qty_expected)}</td>
                  <td className={short ? undefined : 'scan-done'}>{plainQty(l.qty_received)}</td>
                  <td>{l.discrepancy === 'NONE' ? '—' : <span className="badge danger">{discrepancyLabel(t, l.discrepancy)}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {choice && (
        <ul className="scan-pick-meta">
          {choice.candidates.map((c) => <li key={c.skuId} className="meta">{c.code} — {candidateMeta(c)}</li>)}
        </ul>
      )}
    </section>
  );
}
